import React, { useState, useRef, useContext } from 'react';
import { useTranslation } from "react-i18next";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { Tooltip } from 'primereact/tooltip';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import CustomDropdown from '../../../../custom/customDropdown';
import { AuthContext } from '../../../../Header/auth - context';
import { useSelector } from 'react-redux';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const emptyForm = {
    IznosKm: null,
    Valuta: null,
    VazenjeOd: null,
    VazenjeDo: null,
    Napomena: '',
};

const AmortizacijaTab = ({ options, amortizacijaDB, onRefresh, toast }) => {
  const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const company = useSelector(state => state.company);
    const [formData, setFormData] = useState(emptyForm);
    const [selectedId, setSelectedId] = useState(null);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);

    const resetForm = () => { setFormData(emptyForm); setSelectedId(null); setFormSubmitted(false); };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const parseDateStr = (str) => {
        if (!str) return null;
        const parts = str.replace(/\./g, ' ').trim().split(/\s+/);
        if (parts.length < 3) return null;
        const d = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
        return isNaN(d.getTime()) ? null : d;
    };

    const toUtcIso = (d) => {
        if (!d) return null;
        const x = d instanceof Date ? d : new Date(d);
        const utc = new Date(x.getTime() - x.getTimezoneOffset() * 60000);
        return utc.toISOString().slice(0, 19) + 'Z';
    };

    const handleRowSelect = (row) => {
        const id = row?.id;
        setSelectedId(prev => {
            const next = String(prev) === String(id) ? null : id;
            if (next !== null) {
                setFormData({
                    IznosKm: row.IznosKm != null ? Number(row.IznosKm) : null,
                    Valuta: row.Valuta || null,
                    VazenjeOd: parseDateStr(row.VazenjeOd),
                    VazenjeDo: parseDateStr(row.VazenjeDo),
                    Napomena: row.Napomena || '',
                });
            } else {
                setFormData(emptyForm);
                setFormSubmitted(false);
            }
            return next;
        });
    };

    const handleSave = (e) => {
        e.preventDefault();
        setFormSubmitted(true);
        if (!formData.IznosKm || !formData.Valuta || !formData.VazenjeOd) return;
        confirmDialog({
            message: selectedId ? 'Sačuvati izmene konfiguracije amortizacije?' : 'Dodati novu konfiguraciju amortizacije?',
            acceptLabel: 'Da', rejectLabel: 'Ne',
            icon: 'pi pi-info-circle', acceptClassName: 'p-button-danger',
            accept: () => doSave(),
        });
    };

    const doSave = () => {
        const payload = { ...formData, VazenjeOd: toUtcIso(formData.VazenjeOd), VazenjeDo: toUtcIso(formData.VazenjeDo) };
        fetch(process.env.REACT_APP_BACKEND_URL + '/trip/ManageObracunSifarnik', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body: JSON.stringify({ step: 'SAVE_AMORTIZACIJA', id: selectedId || 0, formData: payload }),
        })
            .then(r => r.json().then(d => ({ ok: r.ok, d })))
            .then(({ ok, d }) => {
                if (!ok) throw new Error(d.message || 'Server error');
                const msg = d[0]?.message || '';
                if (msg.includes('Nema izmena')) {
                    toast.current.show({ severity: 'warn', summary: msg, life: 3000 });
                    return;
                }
                toast.current.show({ severity: 'success', summary: msg || 'Uspešno!', life: 3000 });
                resetForm();
                onRefresh();
            })
            .catch(err => toast.current.show({ severity: 'error', summary: t('admin.m_greska_pri_cuvanju'), detail: err?.message || String(err), life: 8000 }));
    };

    const handleDelete = () => {
        if (!selectedId) { toast.current.show({ severity: 'warn', summary: t('admin.m_izaberite_unos_za_brisanje'), life: 3000 }); return; }
        confirmDialog({
            message: 'Obrisati konfiguraciju amortizacije?',
            acceptLabel: 'Da', rejectLabel: 'Ne',
            icon: 'pi pi-info-circle', acceptClassName: 'p-button-danger',
            accept: () => {
                fetch(process.env.REACT_APP_BACKEND_URL + '/trip/ManageObracunSifarnik', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
                    body: JSON.stringify({ step: 'DELETE_AMORTIZACIJA', id: selectedId }),
                })
                    .then(r => r.json())
                    .then(() => { toast.current.show({ severity: 'success', summary: t('admin.m_konfiguracija_amortizacije_obrisana'), life: 3000 }); resetForm(); onRefresh(); })
                    .catch(() => toast.current.show({ severity: 'error', summary: t('admin.m_greska_pri_brisanju'), life: 3000 }));
            },
        });
    };

    const invalid = (val) => formSubmitted && (val === null || val === '' || val === undefined);

    const exportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Amortizacija');
        const headers = ['Iznos/km', 'Valuta', 'Važi od', 'Važi do', 'Napomena'];
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.eachCell(cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
            cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
        });
        (amortizacijaDB || []).forEach(r => {
            const row = worksheet.addRow([
                r.IznosKm != null ? Number(r.IznosKm).toFixed(2) : '',
                r.ValutaNaziv || '',
                r.VazenjeOd || '',
                r.VazenjeDo || '',
                r.Napomena || '',
            ]);
            row.eachCell(cell => {
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });
        worksheet.columns.forEach(col => {
            let max = 10;
            col.eachCell(cell => { if (cell.value) max = Math.max(max, cell.value.toString().length); });
            col.width = max + 2;
        });
        const dt = new Date().toLocaleString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Belgrade' }).replace(/,/g, '');
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `Amortizacija - ${company?.companyName || ''} - ${dt}.xlsx`);
    };

    const tableHeader = (
        <div className="export-buttons fakture-stavke-header">
            <Tooltip target=".export-buttons>button" position="bottom" />
            <div className="fakture-stavke-radnje">
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded onClick={exportExcel} data-pr-tooltip="XLS" />
                {selectedId && <Button type="button" icon="pi pi-trash" severity="danger" rounded onClick={handleDelete} data-pr-tooltip={t('common.delete')} />}
            </div>
            <input type="search" placeholder={t('common.search')} className="p-inputtext p-component"
                onInput={e => setGlobalFilter(e.target.value)} />
        </div>
    );

    return (
        <div style={{ marginTop: '1%' }}>
            <ConfirmDialog />
            <div className="blagajnaDashboard dnevniceDashboard">
                <Card>
                    <form autoComplete="off">
                        <div className="gridLicni">
                            <div className="col-12 md:col-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('admin.iznosPoKm')}:</label>
                                <InputNumber name="IznosKm" value={formData.IznosKm}
                                    onValueChange={handleChange} minFractionDigits={2} maxFractionDigits={2}
                                    required className={invalid(formData.IznosKm) ? 'p-invalid' : ''} />
                            </div>
                            <div className="col-12 md:col-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('nova.valuta')}:</label>
                                <CustomDropdown name="Valuta" value={formData.Valuta}
                                    options={options.ValuteDB} optionLabel="label" placeholder={t('nova.valuta')}
                                    required invalid={invalid(formData.Valuta)} onChange={handleChange} />
                            </div>
                            <div className="col-12 md:col-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('ture.vaziOd')}:</label>
                                <Calendar name="VazenjeOd" value={formData.VazenjeOd} onChange={handleChange}
                                    showIcon dateFormat="dd.mm.yy" mask="99.99.9999"
                                    required className={invalid(formData.VazenjeOd) ? 'p-invalid' : ''} />
                            </div>
                            <div className="col-12 md:col-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>Važi do: <i className="pi pi-info-circle" style={{ marginLeft: '3px', color: '#888' }} title="Prazan = i dalje važi" /></label>
                                <Calendar name="VazenjeDo" value={formData.VazenjeDo} onChange={handleChange}
                                    showIcon dateFormat="dd.mm.yy" mask="99.99.9999" />
                            </div>
                            <div className="col-12 md:col-12" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('nova.napomena')}:</label>
                                <InputText name="Napomena" value={formData.Napomena} onChange={handleChange} />
                            </div>
                        </div>
                        <div className="formUnosZap buttonUnosZap">
                            <button type="submit" onClick={handleSave} style={{ minWidth: 'fit-content', marginTop: '2%' }} className="btn btn-success">
                                {selectedId ? 'SAČUVAJ' : 'UNOS'}
                            </button>
                        </div>
                    </form>
                </Card>
                <Card>
                    <DataTable className="stavke-tabela" value={amortizacijaDB || []} paginator rows={10} scrollable scrollHeight="45vh"
                        header={tableHeader} emptyMessage="Nema konfiguracija amortizacije" resizableColumns
                        globalFilter={globalFilter}
                        rowClassName={row => String(row.id) === String(selectedId) ? 'selected-row' : ''}>
                        <Column header={t('common.choose')} style={{ minWidth: '90px' }}
                            body={rowData => (
                                <Button label={t('common.choose')} icon="pi pi-eye" type="button"
                                    className="p-button-text p-button-sm" onClick={() => handleRowSelect(rowData)} />
                            )} />
                        <Column field="IznosKm" header={t('admin.iznosKm')} sortable style={{ minWidth: '110px' }}
                            body={r => r.IznosKm != null ? `${Number(r.IznosKm).toFixed(2)} ${r.ValutaNaziv || ''}` : '-'} />
                        <Column field="VazenjeOd" header={t('ture.vaziOd')} sortable style={{ minWidth: '110px' }} />
                        <Column field="VazenjeDo" header={t('ture.vaziDo')} sortable style={{ minWidth: '110px' }}
                            body={r => r.VazenjeDo || 'i dalje važi'} />
                        <Column field="Napomena" header={t('nova.napomena')} style={{ minWidth: '200px' }} />
                    </DataTable>
                </Card>
            </div>
        </div>
    );
};

export default AmortizacijaTab;
